<script lang="ts">
    import type { Models } from '@smartlinkdev/types';
    import { format, isWithinInterval, parseISO } from 'date-fns';
    import _ from 'lodash';
    import {goto} from '$app/navigation';
    import {
        forceRoveMap,
        mapInstance,
        venue,
        showDirections,
        destinationId,
        enableDirections,
        currentLocation,
        expandedLocation
    } from '$lib/stores';
    import { t } from '$lib/translations';
    import { sanitizePossibleHTMLContent } from '$lib/utils';

    interface ExtHoursEntry {
        open_time: string;
        close_time: string;
        day_of_week: number;
        open_time_formatted: string;
        close_time_formatted: string;
    }

    export let location: Models.Location;
    let mondayToSundayHours = [];
    let groupedHours;
    let isSeasonal = false;
    let venueSeasonal = $venue.settings?.api?.venueDisplaySeasonalLocations || false;
    const weekdays = [
      $t('content.weekdays.sun'),
      $t('content.weekdays.mon'),
      $t('content.weekdays.tue'),
      $t('content.weekdays.wed'),
      $t('content.weekdays.thu'),
      $t('content.weekdays.fri'),
      $t('content.weekdays.sat'),
    ];

    let url = location.url;
    if (url && !url.includes('http')) {
        url = `http://${url}`;
    }

    let hideBannerImg = $venue.settings?.embed?.details?.hideBannerImg || false;
    let hideLogoImg = $venue.settings?.embed?.details?.hideLogoImg || false;
    let hidePhone = $venue.settings?.embed?.details?.hidePhone || false;
    let hideHours = $venue.settings?.embed?.details?.hideHours || false;
    let hideAddress = $venue.settings?.embed?.details?.hideAddress || false;
    let hideWebsite = $venue.settings?.embed?.details?.hideWebsite || false;
    let startExpanded = $venue.settings?.embed?.details?.startExpanded || false;
    let roveMaps = $venue.settings?.mapType === 'rove' || $forceRoveMap;

    const displaySeasonal = (location: Models.Location, venueIsSeasonal: boolean): boolean => {
        try {
            if (location.seasonal_dates === null || location.seasonal_dates === undefined) {
                return false;
            }
            return venueIsSeasonal && Object.keys(location.seasonal_dates).length > 0;
        } catch (e) {
            return false;
        }
    }
    const withinSeasonalDates = (location: Models.Location): boolean => {
        let yearStart = new Date().getFullYear();
        let yearEnd = yearStart;

        if (location.seasonal_dates?.start.month > location.seasonal_dates?.end.month) {
            yearEnd++; // If it's a year rollover, adjust the end date's year
        }

        const start = `${yearStart}-${location.seasonal_dates?.start.month}-${location.seasonal_dates?.start.day}`;
        const end = `${yearEnd}-${location.seasonal_dates?.end.month}-${location.seasonal_dates?.end.day}`;

        const dates = [parseISO(start), parseISO(end)];
        return isWithinInterval(new Date(), {start: dates[0], end: dates[1]});
    }

    const locSeasonal = displaySeasonal(location, venueSeasonal);
    if (locSeasonal) {
        isSeasonal = withinSeasonalDates(location);
    }

    if (isSeasonal) {
        mondayToSundayHours = location.seasonal_hours_entries.map((hour_entry): ExtHoursEntry => {
            return {
                open_time: hour_entry.open_time,
                close_time: hour_entry.close_time,
                day_of_week: (hour_entry.day_of_week === null) ? 7 : hour_entry.day_of_week,
                open_time_formatted: format(new Date(`2022-01-01 ${hour_entry.open_time}`), 'h:mm aaa'),
                close_time_formatted: format(new Date(`2022-01-01 ${hour_entry.close_time}`), 'h:mm aaa')
            }
        });
        const sorted: ExtHoursEntry[] = _.sortBy(mondayToSundayHours, 'day_of_week', 'open_time');
        groupedHours = _.groupBy(sorted, 'day_of_week');
    } else if (!isSeasonal && location.hours_entries && location.hours_entries.length > 0) {
        mondayToSundayHours = location.hours_entries.map((hour_entry): ExtHoursEntry => {
            return {
                open_time: hour_entry.open_time,
                close_time: hour_entry.close_time,
                day_of_week: (hour_entry.day_of_week === null) ? 7 : hour_entry.day_of_week,
                open_time_formatted: format(new Date(`2022-01-01 ${hour_entry.open_time}`), 'h:mm aaa'),
                close_time_formatted: format(new Date(`2022-01-01 ${hour_entry.close_time}`), 'h:mm aaa')
            }
        });
        const sorted: ExtHoursEntry[] = _.sortBy(mondayToSundayHours, 'day_of_week', 'open_time');
        groupedHours = _.groupBy(sorted, 'day_of_week');
    }

    const directions = () => {
        $showDirections = true;
        $destinationId = `location|${location.id}`;
        $expandedLocation = undefined;
    };

    const minimize = () => {
        $expandedLocation = undefined;
    };

    const close = () => {
        const el = document.getElementById('location-details');
        if (el) el.remove();

        $expandedLocation = undefined;
        goto(`/venues/${$venue.id}`);

        if ($mapInstance && $mapInstance.removeHighlights) {
            $mapInstance.removeHighlights();
        }
    };

    const urlClick = () => {
        if (url) {
            window.open(url, '_blank')?.focus();
        }
    };
</script>

<div class="location-details-full">
    <div class="location-details-full-inside">
        <div class="grid grid-cols-[20px_1fr_20px] place-items-center">
            <div id="expand-btn" on:click={minimize}>
                <span class="material-symbols-outlined cursor-pointer">pan_zoom</span>
            </div>
            <div class="flex-grow text-center px-2 text-xl font-bold text-gray-500">
                {location.name}
            </div>
            <div id="close-btn" on:click={close}>
                <span class="material-symbols-outlined cursor-pointer">cancel</span>
            </div>
        </div>
        <div id="full-details" class="location-details-inner">
            {#if location.location_types}
                {#each location.location_types.filter((c) => c.highlight) as category}
                    <div class="highlight-category">{category.name}</div>
                {/each}
            {/if}
            {#if location.logo_img && !hideLogoImg}
                <div class="location-logo-img w-full max-h-24 grid place-items-center my-4">
                    <img src={location.logo_img} class="max-h-24" style="object-fit: contain;"/>
                </div>
            {/if}
            <div id="more-details" class="mt-2">
                {#if location.phone && !hidePhone}
                    <h3>{ $t('content.phoneNumber') }</h3>
                    <div class="details-piece">{location.phone}</div>
                {/if}
                {#if location.url && !hideWebsite}
                    <h3>{ $t('content.website') }</h3>
                    <div class="details-piece cursor-pointer" id="details-url" on:click={urlClick}>
                        {location.url}
                    </div>
                {/if}
                {#if !hideHours && (location.hours || groupedHours)}
                    <h3>{ $t('content.hours') } <span class="text-gray-500">{ $t('content.hoursMayVary') }</span></h3>
                    <div class="details-piece">
                        {#if groupedHours}
                            <div class="grid grid-cols-[150px_1fr]">
                                {#each Object.keys(groupedHours) as day_number }
                                    <div>
                                        <b>{weekdays[day_number]}</b>
                                    </div>
                                    <div>
                                        {#each groupedHours[day_number] as entry}
                                            <div>{entry.open_time_formatted} - {entry.close_time_formatted}</div>
                                        {/each}
                                    </div>
                                {/each}
                            </div>
                        {:else}
                            {@html location.hours.replaceAll('\n', '<br />') }
                        {/if}
                    </div>
                {/if}
                {#if !hideAddress && location.address1}
                    <h3>{ $t('content.address') }</h3>
                    <div class="details-piece">
                        {location.address1}{ location.city ? `, ${location.city}` : '' }{ location.state ? `, ${location.state}` : '' } { location.zip ? `${location.zip}` : '' }
                    </div>
                {/if}
                <h3>{ $t('content.description') }</h3>
            </div>
            <div class="details-description">
                <div>
                    {@html sanitizePossibleHTMLContent(location.description)}
                </div>
            </div>
            {#if location.banner_img && !hideBannerImg}
                <div id="bannerImage" class="location-logo-img w-full grid place-items-center my-4">
                    <img src={location.banner_img} class="max-h-48" style="object-fit: contain; border-radius: 0.8vh"/>
                </div>
            {/if}
            {#if $enableDirections}
                <div class="grid grid-cols-[50%_50%] gap-1 mt-4">
                    <div class="mx-1">
                        <button id="details-btn" class="bottom-button more-button" on:click={minimize}>
                            { $t('content.lessInfo') }
                        </button>
                    </div>
                    <div class="mx-1">
                        <button id="directions-btn" class="bottom-button directions-button" on:click={directions}>
                            { $t('content.getDirections') }
                        </button>
                    </div>
                </div>
            {:else}
                <div class="grid grid-cols-[100%] gap-1 mt-4">
                    <div class="mx-1">
                        <button id="details-btn" class="bottom-button more-button" on:click={minimize}>
                            { $t('content.lessInfo') }
                        </button>
                    </div>
                </div>
            {/if}
        </div>
    </div>
</div>
<style lang="postcss">
    .location-details-full {
        pointer-events: none;
        @apply fixed right-40 z-50 grid place-items-center top-[5vh] h-[90vh];
    }

    :global(.leftMenu .location-details-full) {
        right: unset;
        @apply left-40;
    }

    .location-details-full-inside {
        pointer-events: all;
        @apply p-4 rounded-2xl bg-white text-gray-800 overflow-y-auto transition-transform md:w-[390px] lg:w-[420px] xl:w-[450px] shadow-xl;
        max-height: 90vh;
        min-height: 20vh;
    }

    .more-button {
        @apply bg-[#ed736f];
    }

    .directions-button {
        @apply bg-[#475883];
    }

    .bottom-button {
        @apply text-white text-center py-2 w-full rounded-2xl;
    }

    .location-details-tooltip h3 {
        @apply font-bold text-lg mb-1;
    }

    .details-piece {
        @apply mb-6;
    }

    .details-description {
        max-height: 10vh;
        overflow-y: auto;
        padding-right: 4px;
        --sb-track-color: #ccc;
        --sb-thumb-color: #aaa;
        --sb-size: 8px;
    }

    :global(.highlight-category) {
        @apply my-1 p-2 rounded-xl w-[98%] text-white bg-gray-300 text-center font-bold;
    }
</style>
